/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Inject, Injectable } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonActions, FormActionPipelines, FormFlowState } from '@gh/builder-shared';
import { ActionFlow, getDialogInput, State } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { isQuantityEquals, Quantity } from '@gh/core-data';
import {
  DialogService,
  FormMode,
  NotificationService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { map$ } from '@gh/rx/operators';
import { QuoteDetailsBusinessService } from '../../resources/quote-details-business.service';
import { QuoteHousePlanTopList } from '../../resources/quote.model';
import {
  queryQuoteTop,
  queryQuoteTopsHasErrors,
  queryQuoteTopsSaveModel,
  QuoteTopDialogState,
  QuoteTopDialogStateEventType,
} from './quote-top-dialog.state';

type QuoteTopDialogSaveFlowState = FormFlowState<QuoteTopDialogState> & { saveResponse?: QuoteHousePlanTopList };

@Injectable()
export class QuoteTopDialogCommandService {

  constructor(@Inject(MAT_DIALOG_DATA) private dialogData: any,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private state: State<QuoteTopDialogState>,
              private businessService: QuoteDetailsBusinessService,
              private dialogRef: MatDialogRef<any>,
              private dialogs: DialogService,
              private notificationService: NotificationService) {

  }

  get housePlanId(): ID {
    const { housePlanId } = getDialogInput(this.dialogData) as any;

    return housePlanId;
  }

  get formMode(): FormMode {
    const { formMode } = getDialogInput(this.dialogData) as any;

    return formMode;
  }

  load(priceBookId: ID, tops: QuoteHousePlanTopList, formMode: any): void {
    this.state.mutate({ type: QuoteTopDialogStateEventType.Load, priceBookId, tops, formMode });
  }

  setTopValue(topId: ID, value: Quantity<any>): void {
    const top = this.state.project((s) => queryQuoteTop(s, topId));

    if (top && !isQuantityEquals(top.value, value)) {
      // set top value temporarily
      this.state.mutate({ type: QuoteTopDialogStateEventType.SetTopValue, topId, value });

      if (top.isFromGto) {
        this.dialogs.confirmation(yesNoConfirmation('msg_top_gto_remove'))
          .subscribe((answer) => {
            // commit change if user has confirmed change, otherwise rollback original value
            if (yesAnswer(answer)) {
              this.state.mutate({ type: QuoteTopDialogStateEventType.CommitTop, topId });
            } else {
              this.state.mutate({ type: QuoteTopDialogStateEventType.ResetTop, top });
            }
          });
      } else {
        this.state.mutate({ type: QuoteTopDialogStateEventType.CommitTop, topId });
      }
    }

  }

  resetFocusedCell(): void {
    this.state.mutate({ type: QuoteTopDialogStateEventType.ResetFocusedCell });
  }

  processFlow(): ActionFlow<QuoteTopDialogSaveFlowState> {
    return this.pipelines.process({
      validate: this.commonActions.validateWithEffect(
        () => this.state.project(queryQuoteTopsHasErrors),
        () => this.handleErrors()),
      execute: this.commonActions.async((state) =>
        this.businessService.updateTops(this.housePlanId, this.state.project(queryQuoteTopsSaveModel)).pipe(
          map$((saveResponse) => ({ ...state, saveResponse })))),
      finish: () => this.dialogRef.close(true),
    });
  }

  recalculateFlow(): ActionFlow<QuoteTopDialogSaveFlowState> {
    return this.pipelines.recalculate({
      validate: this.commonActions.validateWithEffect(
        () => this.state.project(queryQuoteTopsHasErrors),
        () => this.handleErrors()),
      execute: this.commonActions.async((state) =>
        this.businessService.recalculateTops(this.housePlanId, this.state.project(queryQuoteTopsSaveModel)).pipe(
          map$((saveResponse) => ({ ...state, saveResponse })))),
      finish: ({ saveResponse }: QuoteTopDialogSaveFlowState) =>
        this.state.mutate({ type: QuoteTopDialogStateEventType.Merge, tops: saveResponse }),
    });
  }

  private handleErrors(): void {
    this.notificationService.error('msg_he_incorrect_data_in_tables');

    this.state.mutate({ type: QuoteTopDialogStateEventType.NavigateToInvalid });
  }
}
